import axios from 'axios';
import ApiService from './ApiService';

/**
 * Panel settings client.
 *
 * Separate from ApiService.js for the same reason TelnaApiService is: that file
 * is a 3000-line surface every page imports, and settings will keep gaining
 * endpoints. The base URL is reused from it rather than re-declared, because
 * that is where the dev/prod switch lives.
 */
const authHeaders = () => ({
    'Content-Type': 'application/json',
    Authorization: 'Bearer ' + localStorage.getItem('token'),
});

const base = () => `${ApiService.baseURL}/settings`;

/**
 * Unwrap an axios error, keeping `code` and `blockers`.
 *
 * `blockers` is the whole point of the 409 the provider switch can return — it
 * is the list of reasons the switch was refused, and a page that drops it can
 * only say "failed" where the backend said exactly what was missing.
 */
const toError = (error, fallback) => {
    const d = error?.response?.data;
    const err = new Error(d?.message || d?.error || error.message || fallback);
    err.code = d?.code || 'REQUEST_FAILED';
    err.status = error?.response?.status;
    err.blockers = d?.blockers || [];
    return err;
};

class SettingsApiService {
    /** Current eSIM provider, the options, and whether a switch is possible. */
    static async getEsimProvider() {
        try {
            const res = await axios.get(`${base()}/esim-provider`, { headers: authHeaders() });
            return res.data;
        } catch (error) { throw toError(error, 'Could not read the eSIM provider setting'); }
    }

    /**
     * What a mode WOULD do to the catalogue: how many plans route to each
     * provider, why the rest did not, and the biggest savings.
     *
     * Read-only and buys nothing. The preview deliberately ignores whether
     * Telna can deliver today, so the rate card can be evaluated before
     * purchases are ever switched on.
     */
    static async getRoutingPreview(mode = 'auto') {
        try {
            const res = await axios.get(`${base()}/esim-provider/preview`, {
                headers: authHeaders(),
                params: { mode },
            });
            return res.data;
        } catch (error) { throw toError(error, 'Could not build the routing preview'); }
    }

    /**
     * Open or close /new-esims for direct visits.
     *
     * A TESTING switch, separate from the provider on purpose: `provider`
     * decides what every customer gets when they tap eSIM, and the only way
     * to try the per-megabyte shop should not be to point all of them at it.
     * It changes what is visible, never what is sellable — checkout still
     * gates on deliverability and stock.
     */
    static async setPerMbPreview(enabled) {
        try {
            const res = await axios.put(
                `${base()}/esim-provider`,
                { perMbPreview: Boolean(enabled) },
                { headers: authHeaders() },
            );
            return res.data;
        } catch (error) { throw toError(error, 'Could not change the preview setting'); }
    }

    /** Switch the provider for NEW sales. Existing orders are unaffected. */
    static async setEsimProvider(provider) {
        try {
            const res = await axios.put(`${base()}/esim-provider`, { provider }, { headers: authHeaders() });
            return res.data;
        } catch (error) { throw toError(error, 'Could not switch the eSIM provider'); }
    }
}

export default SettingsApiService;
